import { useSearchPlayersMutation, useInvitePlayerMutation } from '@http';
import { TextInput } from '@components/ui/TextInput';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface InvitePlayerModalProps {
  children: React.ReactNode;
  className?: string;
};

export function InvitePlayerModal({ children, className }: InvitePlayerModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState<{ username: string; id: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [invited, setInvited] = useState<string[]>([]);

  const { t } = useTranslation(undefined, { keyPrefix: 'Lobby.PlayerInvite' });
  const { mutateAsync: searchAsync } = useSearchPlayersMutation();
  const { mutateAsync: inviteAsync } = useInvitePlayerMutation();

  useEffect(() => {
    if (!isOpen) return;
    searchAsync({ name: '' }, {
      onSuccess: (data) => setPlayers(data ?? []),
    });
  }, [isOpen]);

  useEffect(() => {
    setPlayers([]);
    setIsLoading(true);

    const timeout = setTimeout(() => {
      if (!search.length) return setIsLoading(false);

      searchAsync({ name: search }, {
        onSuccess: (data) => {
          setPlayers(data ?? []);
          setIsLoading(false);
        },
        onError: () => setIsLoading(false),
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [search]);

  const invitePlayer = async (playerId: string) => {
    if (invited.includes(playerId)) return;

    const data = await inviteAsync({ id: playerId });
    if (!data?.success) {
      toast(t('playerOffline'), { type: 'error' });
    } else {
      setInvited([...invited, playerId]);
    }
  };

  const close = () => {
    setPlayers([]);
    setSearch('');
    setInvited([]);
    setIsOpen(false);
  };

  return (
    <>
      <span onClick={() => setIsOpen(true)} className={className}>
        {children}
      </span>
      <Modal onRequestClose={close} title={t('title')} isOpen={isOpen}>
        <TextInput placeholder={t('search')} onChange={setSearch} className="w-full" value={search} />

        {isLoading && (
          <div className="flex justify-center py-12">
            <span className="loader" />
          </div>
        )}

        {!isLoading && !players.length && (
          <div className="flex justify-center py-12">
            <p className="text-sm">{t(search ? 'noResults' : 'pleaseEnter')}</p>
          </div>
        )}

        {players.map((player, i) => (
          <div
            className={`${i < players.length - 1 && 'border-b border-secondary'} flex items-center justify-between p-2`}
            key={player.id}
          >
            <p>{player.username}</p>
            <Button onClick={() => invitePlayer(player.id)} className="w-auto py-1">
              {t(invited.includes(player.id) ? 'invited' : 'invite')}
            </Button>
          </div>
        ))}
      </Modal>
    </>
  );
}